import React, { useState } from 'react';
import {
  EyeOutlined, ReconciliationOutlined, WhatsAppOutlined, UserOutlined,
  CalendarOutlined, DollarOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { Button, Space, Popover, Popconfirm, Tag } from 'antd';
import { format } from 'date-fns';
import {
  NdoeActionButton, NdoeDataCard, NdoeImage,
} from '../../components/ndoe';
import { resolveImageUrl, resolveImageVariant } from '../../components/ndoe/imageVariant';

/*
  « CETTE COMMANDE CONTIENT UN ÉCHANGE ».

  Signalé par la propriétaire le 21/08/2026 : la dernière opération avec
  Aminata était un échange, et Nobel la voyait comme une vente ordinaire.

  L'ARGENT, LUI, ÉTAIT JUSTE — vérifié sur la commande 1558 : la vente à 65 $
  puis celle à 100 $ ont bien été annulées, seule celle à 90 $ reste active, et
  la dette a suivi. Il ne manquait que le mot pour le dire à l'écran.

  « Contient » et non « est » : la ligne représente une COMMANDE (la requête
  agrège par `commande.id_commande`), qui peut mêler une vente ordinaire et un
  échange. Dire « Échange » tout court serait faux sur une commande mixte.
*/
const BadgeEchange = ({ record }) => (
  Number(record.contient_echange) === 1 ? (
    <Tag color="purple" style={{ marginInlineEnd: 0, fontWeight: 600 }}
      title="Cette commande comporte au moins une paire échangée. Le montant affiché est celui de la paire finale : les ventes remplacées ont été annulées.">
      ⇄ Échange
    </Tag>
  ) : null
);

/*
  Colonnes & cartes PARTAGÉES des listes de ventes (onglets « Ventes » et
  « Ventes du jour ») — une seule définition pour que les deux vues restent
  cohérentes.

  Lecture des chiffres (la requête /api/vente agrège PAR COMMANDE) :
  - total_varianteproduit = SUM(quantite)   -> nombre de PAIRES vendues
  - nombre_vendu          = COUNT(lignes)   -> nombre d'ARTICLES (lignes de vente)
  - nb_modeles            = COUNT(DISTINCT produit) -> modeles DIFFERENTS
    Les colonnes d affichage (nom, photo, pointure, reference) viennent toutes
    de la MEME ligne representative cote serveur : elles sont coherentes entre
    elles et stables. Avant le 23/08/2026, chacune etait choisie au hasard.
  Les anciennes colonnes « Qté vendue » / « Nbre vendu » affichaient presque
  toujours le même chiffre (quantité 1 par ligne) : fusionnées en UNE colonne
  « Paires vendues », le nombre d'articles passe en sous-texte quand il diffère.
*/

// Photo de l'article au survol : elle apparaît quand le curseur reste ≥ 1 s
// (mouseEnterDelay est en secondes chez AntD). Sans photo, cellule inchangée.
// Photo du popover, avec repli dérivé → original (sinon un dérivé manquant
// afficherait une image CASSÉE pendant le rattrapage des vignettes).
const PhotoSurvol = ({ record }) => {
  const [replie, setReplie] = useState(false);
  const src = replie ? resolveImageUrl(record.img) : resolveImageVariant(record.img, 220, 'auto');
  return (
    <img
      src={src}
      alt={record.nom_produit || 'Article'}
      onError={() => !replie && setReplie(true)}
      style={{ width: 220, maxWidth: '60vw', borderRadius: 10, display: 'block' }}
    />
  );
};

const withPhotoHover = (record, children) => {
  // 220 px d'affichage : le dérivé 384 (ou 768 en écran dense) suffit largement
  // — l'original ~1254 px n'apportait rien de visible dans un popover.
  const url = resolveImageVariant(record.img, 220, 'auto');
  if (!url) return children;
  return (
    <Popover
      mouseEnterDelay={1}
      placement="rightTop"
      content={
        <div style={{ maxWidth: 236 }}>
          <PhotoSurvol record={record} />
          {Number(record.nombre_vendu) > 1 && (
            <small style={{ color: '#8c8c8c', display: 'block', marginTop: 6 }}>
              Photo du 1er des {record.nb_modeles || record.nombre_vendu} modèles de la commande (👁 pour tout voir).
            </small>
          )}
        </div>
      }
    >
      {children}
    </Popover>
  );
};

const usd = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const buildVenteColumns = ({ user, onOpenClient, onOpenVente, onTicket, onDelete }) => [
  { title: '#', dataIndex: 'id', key: 'id', render: (text, record, index) => index + 1, width: '3%' },
  {
    title: 'Client',
    dataIndex: 'nom_client',
    key: 'nom_client',
    render: (text, record) => (
      <div onClick={() => onOpenClient(record.id_client)} style={{ cursor: 'pointer' }}>
        <Tag color="green"><UserOutlined style={{ marginRight: 5 }} />{text}</Tag>
      </div>
    ),
  },
  {
    title: 'Livreur',
    dataIndex: 'username',
    key: 'username',
    render: (text) => <Tag color="green"><UserOutlined style={{ marginRight: 5 }} />{text}</Tag>,
  },
  {
    // Mandat §6 : référence toujours visible à côté du libellé. La ligne montrée
    // est UN des articles du groupe (« + N autres ») — le détail complet reste
    // à un clic (œil -> VenteView). Survol ≥ 1 s : la photo apparaît.
    title: 'Article',
    dataIndex: 'nom_produit',
    key: 'article',
    render: (text, record) => withPhotoHover(record, (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {(record.code_article || record.code_produit) && (
          <span className="num" style={{ color: '#8a5a10', fontWeight: 700, fontSize: 12 }}>
            {record.code_article || record.code_produit}
          </span>
        )}
        <span>{text || '—'}{record.pointure ? ` · P${record.pointure}` : ''}</span>
        {/* Sous le libellé, pas à côté : la colonne est déjà dense, et le badge
            doit se lire sans repousser la référence hors du champ. */}
        {Number(record.contient_echange) === 1 && (
          <span style={{ marginTop: 3 }}><BadgeEchange record={record} /></span>
        )}
        {/*
          « + N AUTRES MODÈLES » PLUTÔT QUE « + N AUTRES LIGNES ».

          Deux lignes de la même commande peuvent porter le même modèle en deux
          pointures : dire « lignes » laissait croire à deux articles distincts.
          Le serveur compte désormais les modèles DIFFÉRENTS (nb_modeles), ce qui
          est la question qu'on se pose en lisant la liste.

          Mesuré le 23/08/2026 : 341 commandes sur 1 315 en portent plusieurs —
          une sur quatre. Sur celles-là, le nom affiché est UN article, pas le
          contenu de la commande, alors que le montant, lui, couvre tout.

          Repli sur `nombre_vendu` si `nb_modeles` manque : un écran servi par
          une API plus ancienne ne doit pas perdre l'indication.
        */}
        {(Number(record.nb_modeles) > 1 || (!record.nb_modeles && Number(record.nombre_vendu) > 1)) && (
          <small style={{ color: '#8c8c8c' }}>
            {record.nb_modeles
              ? `+ ${Number(record.nb_modeles) - 1} autre${Number(record.nb_modeles) > 2 ? 's' : ''} modèle${Number(record.nb_modeles) > 2 ? 's' : ''} — voir 👁`
              : `+ ${Number(record.nombre_vendu) - 1} autre(s) ligne(s) — voir 👁`}
          </small>
        )}
      </div>
    )),
  },
  {
    title: 'Téléphone',
    dataIndex: 'telephone',
    key: 'telephone',
    render: (text) => (
      <Popover content="Discutez avec lui sur WhatsApp" placement="top">
        <Tag
          onClick={() => window.open(`https://wa.me/${String(text || '').replace(/[^0-9]/g, '')}`, '_blank')}
          color="blue"
          style={{ cursor: 'pointer' }}
        >
          <WhatsAppOutlined style={{ color: 'green', marginRight: 5 }} />
          {text}
        </Tag>
      </Popover>
    ),
  },
  {
    title: 'Paires vendues',
    dataIndex: 'total_varianteproduit',
    key: 'total_varianteproduit',
    sorter: (a, b) => a.total_varianteproduit - b.total_varianteproduit,
    sortDirections: ['descend', 'ascend'],
    render: (text, record) => {
      const paires = Number(record.total_varianteproduit) || 0;
      const lignes = Number(record.nombre_vendu) || 0;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Tag color="green" style={{ width: 'fit-content' }}>{paires} paire{paires > 1 ? 's' : ''}</Tag>
          {lignes > 1 && <small style={{ color: '#8c8c8c' }}>{lignes} articles différents</small>}
        </div>
      );
    },
  },
  {
    title: 'Montant',
    dataIndex: 'total_prix_vente',
    key: 'total_prix_vente',
    sorter: (a, b) => a.total_prix_vente - b.total_prix_vente,
    sortDirections: ['descend', 'ascend'],
    render: (text, record) => (
      <Tag color="green" icon={<DollarOutlined />}>{usd(record.total_prix_vente)}</Tag>
    ),
  },
  {
    title: 'Date',
    dataIndex: 'date_vente',
    key: 'date',
    sorter: (a, b) => new Date(a.date_vente) - new Date(b.date_vente),
    sortDirections: ['descend', 'ascend'],
    render: (text) => (
      <Tag color="blue" icon={<CalendarOutlined />}>
        {text ? format(new Date(text), 'dd-MM-yyyy') : '—'}
      </Tag>
    ),
  },
  {
    title: 'Action',
    key: 'action',
    render: (text, record) => (
      <Space size="middle">
        <Popover title="Voir la liste de vente de cette commande" trigger="hover">
          <Button icon={<EyeOutlined />} style={{ color: 'blue' }} onClick={() => onOpenVente(record.id_commande)} />
        </Popover>
        <Popover title="Ticket" trigger="hover">
          <Button icon={<ReconciliationOutlined />} style={{ color: 'blue' }} onClick={() => onTicket(record.id_commande)} />
        </Popover>
        {user?.role === 'admin' && (
          <Popover title="Supprimer" trigger="hover">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer?"
              onConfirm={() => onDelete(record.id_vente)}
              okText="Oui"
              cancelText="Non"
            >
              <Button icon={<DeleteOutlined />} style={{ color: 'red' }} />
            </Popconfirm>
          </Popover>
        )}
      </Space>
    ),
  },
];

// Carte "ligne" pour le mobile. Le prix (finance) reste STRICTEMENT derrière
// canFinance, comme la colonne Montant côté desktop (Lot 0). Pas de survol au
// doigt : la photo est directement sur la carte (tap = zoom plein écran).
export const buildVenteCardRenderer = ({ user, canFinance, onOpenVente, onTicket, onDelete }) => {
  const renderVenteCard = (record) => {
    const code = record.code_article || record.code_produit;
    const articleLabel = `${record.nom_produit || '—'}${record.pointure ? ` · P${record.pointure}` : ''}`;
    const phone = record.telephone;
    const paires = Number(record.total_varianteproduit) || 0;
    const lignes = Number(record.nombre_vendu) || 0;

    const metrics = [{ value: paires, label: paires > 1 ? 'paires' : 'paire' }];
    if (lignes > 1) metrics.push({ value: lignes, label: 'articles' });
    metrics.push({ value: record.date_vente ? format(new Date(record.date_vente), 'dd-MM-yyyy') : '—', label: 'Date' });
    if (canFinance) metrics.push({ value: usd(record.total_prix_vente), label: 'Montant' });

    const actions = [
      <NdoeActionButton key="v" size="sm" variant="ink" icon={<EyeOutlined />} onClick={() => onOpenVente(record.id_commande)}>Voir</NdoeActionButton>,
      <NdoeActionButton key="t" size="sm" variant="secondary" icon={<ReconciliationOutlined />} onClick={() => onTicket(record.id_commande)}>Ticket</NdoeActionButton>,
      phone && <NdoeActionButton key="w" size="sm" variant="secondary" icon={<WhatsAppOutlined />} as="a" href={`https://wa.me/${String(phone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer">WhatsApp</NdoeActionButton>,
      user?.role === 'admin' && (
        <Popconfirm key="d" title="Êtes-vous sûr de vouloir supprimer?" onConfirm={() => onDelete(record.id_vente)} okText="Oui" cancelText="Non">
          <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />}>Suppr.</NdoeActionButton>
        </Popconfirm>
      ),
    ].filter(Boolean);

    return (
      <NdoeDataCard
        media={
          /* stopPropagation : le tap sur la photo zoome sans ouvrir la commande */
          <span onClick={(e) => e.stopPropagation()}>
            <NdoeImage src={record.img} alt={articleLabel} size="lg" />
          </span>
        }
        title={record.nom_client}
        subtitle={`${code ? code + ' · ' : ''}${articleLabel}${record.username ? ' · ' + record.username : ''}`}
        /* L'emplacement de badge de la fiche était libre : sur téléphone, une
           mention noyée dans le sous-titre ne se voit pas. */
        badge={<BadgeEchange record={record} />}
        metrics={metrics}
        onClick={() => onOpenVente(record.id_commande)}
        actions={actions}
      />
    );
  };
  return renderVenteCard;
};

// Foin de recherche d'une ligne : TOUT ce qui identifie la vente sur l'écran.
// La pointure y figure sous ses deux formes (« 38 » et « P38 ») parce que c'est
// « P38 » qui est affiché, mais « 38 » qu'on a en tête.
const foinDe = (item) => [
  item.nom_client,
  item.nom_marque,
  item.nom_produit,
  item.code_produit,
  item.code_article,
  item.username,
  item.pointure,
  item.pointure ? `P${item.pointure}` : '',
  // Taper « echange » dans la zone de recherche doit ramener ces commandes :
  // sans cela, le badge se voit mais ne se cherche pas, et retrouver « les
  // échanges du mois » redevient un travail de défilement. Les deux
  // orthographes parce que l'accent ne se tape pas toujours.
  Number(item.contient_echange) === 1 ? 'echange échange' : '',
].filter(Boolean).join(' ').toLowerCase();

// Filtre de recherche commun aux deux onglets (client, article, code article/
// produit, livreur, marque, pointure).
//
// Chaque mot doit correspondre (ET), comme dans la recherche des commandes :
// « elie nike » trouve les Nike livrées par Élie. C'est ce qui rend la zone
// libre combinable, donc utile — sinon elle ne vaut pas mieux qu'un seul filtre.
export const venteSearchFilter = (searchValue) => {
  const mots = (searchValue || '').toLowerCase().split(/\s+/).filter(Boolean);
  return (item) => {
    if (!mots.length) return true;
    const foin = foinDe(item);
    return mots.every((m) => foin.includes(m));
  };
};
